import type { ReactNode } from 'react'
import { forwardRef } from 'react'

import Box from '@mui/material/Box'
import type { BoxProps } from '@mui/material/Box'
import type { SxProps, Theme } from '@mui/material/styles'

/**
 * MUI v9 の新 Grid は size 通りの固定幅（max-width）を当てず、コンテンツ幅で
 * 折り返す挙動になり既存レイアウトが崩れる。旧 Grid（v5/v6）と同等の
 * 「負マージン + padding ガター」モデルを flexbox で再実装した互換 Grid。
 *
 * codemod 変換後の新 API（container / size / spacing / offset / columns / direction）を
 * そのまま受け取れる。
 */

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
const BREAKPOINTS: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl']

type SizeValue = number | 'grow' | 'auto' | boolean
type Responsive<T> = T | Partial<Record<Breakpoint, T>>

export type GridProps = {
  container?: boolean
  size?: Responsive<SizeValue>
  offset?: Responsive<number>
  columns?: number
  spacing?: number | string
  rowSpacing?: number | string
  columnSpacing?: number | string
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse'
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse'
  children?: ReactNode
  // component="form" 等で渡される追加の HTML 属性（autoComplete/onSubmit など）を透過
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [prop: string]: any
} & Omit<BoxProps, 'direction'>

const toResponsive = <T,>(value: Responsive<T> | undefined): Partial<Record<Breakpoint, T>> | undefined => {
  if (value === undefined) return undefined
  if (typeof value === 'object' && value !== null) return value as Partial<Record<Breakpoint, T>>
  return { xs: value as T }
}

const widthForSize = (size: SizeValue, columns: number): Record<string, unknown> => {
  if (size === 'grow' || size === true) {
    return { flexBasis: 0, flexGrow: 1, maxWidth: '100%' }
  }
  if (size === 'auto' || size === false || size === undefined) {
    return { flexBasis: 'auto', flexGrow: 0, maxWidth: 'none', width: 'auto' }
  }
  const pct = `${(100 * (size as number)) / columns}%`
  return { flexBasis: pct, flexGrow: 0, maxWidth: pct }
}

const buildItemSx =
  (size: GridProps['size'], offset: GridProps['offset'], columns: number): SxProps<Theme> =>
  (theme: Theme) => {
    const sizeMap = toResponsive(size)
    const offsetMap = toResponsive(offset)
    const result: Record<string, unknown> = {
      boxSizing: 'border-box',
      margin: 0,
      // size 未指定時はコンテンツ幅（旧 <Grid item> 相当）
      ...(sizeMap ? {} : { flexBasis: 'auto', flexGrow: 0 }),
    }
    BREAKPOINTS.forEach((bp) => {
      const styles: Record<string, unknown> = {}
      if (sizeMap && sizeMap[bp] !== undefined) Object.assign(styles, widthForSize(sizeMap[bp]!, columns))
      if (offsetMap && offsetMap[bp] !== undefined) {
        styles.marginLeft = offsetMap[bp] === 0 ? 0 : `${(100 * (offsetMap[bp] as number)) / columns}%`
      }
      if (Object.keys(styles).length === 0) return
      if (bp === 'xs') Object.assign(result, styles)
      else result[theme.breakpoints.up(bp)] = styles
    })
    return result
  }

export const Grid = forwardRef<HTMLDivElement, GridProps>(function Grid(
  { container, size, offset, columns = 12, spacing, rowSpacing, columnSpacing, direction, wrap, sx, children, ...rest },
  ref
) {
  if (container) {
    const rSpace = rowSpacing ?? spacing ?? 0
    const cSpace = columnSpacing ?? spacing ?? 0
    const containerSx: SxProps<Theme> = (theme: Theme) => {
      const r = typeof rSpace === 'number' ? theme.spacing(rSpace) : rSpace
      const c = typeof cSpace === 'number' ? theme.spacing(cSpace) : cSpace
      return {
        display: 'flex',
        flexDirection: direction ?? 'row',
        flexWrap: wrap ?? 'wrap',
        boxSizing: 'border-box',
        width: `calc(100% + ${c})`,
        marginLeft: `calc(-1 * ${c})`,
        marginTop: `calc(-1 * ${r})`,
        '& > *': {
          paddingLeft: c,
          paddingTop: r,
          boxSizing: 'border-box',
        },
      }
    }
    return (
      <Box ref={ref} sx={[containerSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]} {...rest}>
        {children}
      </Box>
    )
  }

  const itemSx = buildItemSx(size, offset, columns)
  return (
    <Box ref={ref} sx={[itemSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]} {...rest}>
      {children}
    </Box>
  )
})
